import React from 'react';
import { Link } from 'react-router-dom';
import { Chip, FormControl, InputLabel } from '@material-ui/core';
import { format } from 'date-fns';
import { legacyParse, convertTokens } from '@date-fns/upgrade/v2';

import { primaryBlue, red } from 'utils/color-styles';
import { currencyFormat } from 'utils/currencyFormat';
import DonationCreditRule from 'entities/donationCreditRule/model';
import config from 'config';
import Api from 'entities/api';

interface DonationCreditRuleCompactProps {
  donationCreditRule: DonationCreditRule;
  budgetInactive: boolean;
}

const DonationCreditRuleCompact: React.FC<DonationCreditRuleCompactProps> = ({
  donationCreditRule,
  budgetInactive,
}) => {
  const now = new Date();
  const startingAt = donationCreditRule.get('startingAt');
  const endingAt = donationCreditRule.get('endingAt');

  const balance = donationCreditRule.get('limitTotal')
    ? donationCreditRule.get('limitTotal') - donationCreditRule.get('totalIssued')
    : undefined;

  const inactive =
    budgetInactive || (endingAt && endingAt < now) || (startingAt && startingAt > now);
  const balanceLow =
    donationCreditRule.get('limitTotal') &&
    !inactive &&
    balance &&
    balance / donationCreditRule.get('limitTotal') < 0.15;

  const opacity = inactive ? '0.5' : '1';
  const volunteeringRule = donationCreditRule.type === 'VolunteerTimeDonationCreditRule';

  const chipLabel =
    donationCreditRule.get('type') === 'VolunteerTimeDonationCreditRule'
      ? 'Volunteering Credit'
      : 'Donation Credit';

  return (
    <div
      key={donationCreditRule.id}
      style={{
        border: `1px solid rgba(0, 0, 0, ${inactive ? '0.1' : '0.2'})`,
        padding: '10px 30px 15px 30px',
        marginBottom: 30,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 0,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            opacity,
          }}
        >
          <h3>{donationCreditRule.title}</h3>
          <Chip color="primary" label={chipLabel} style={{ marginLeft: 20 }} />
          <Chip label={donationCreditRule.currencyCode} style={{ marginLeft: 20 }} />
        </div>
        <div>
          <a // eslint-disable-line jsx-a11y/anchor-is-valid, jsx-a11y/click-events-have-key-events
            style={{
              marginRight: 16,
              color: primaryBlue,
              textDecoration: 'underline',
            }}
            onClick={(e) => {
              const a = e.target.tagName === 'A' ? e.target : e.target.parentElement;

              if (!a.href) {
                fetch(`${config.apiUrl}/api/donationCreditRules/${donationCreditRule.id}/report`, {
                  method: 'GET',
                  headers: {
                    Authorization: Api.defaultHeaders.Authorization,
                  },
                })
                  .then((response) => response.blob())
                  .then((blob) => {
                    const url = window.URL.createObjectURL(blob);
                    a.href = url;
                    a.download = `${donationCreditRule.title}-${new Date().toISOString()}.csv`;
                    a.click();
                    window.URL.revokeObjectURL(url);
                    a.removeAttribute('href');
                  });
              }
            }}
            role="button"
            tabIndex="0"
            title="Download report"
          >
            Download report
          </a>
          {donationCreditRule.isEditable ? (
            <Link
              to={`/donations/credit-rule/${donationCreditRule.id}`}
              style={{
                color: primaryBlue,
                textDecoration: 'underline',
                opacity: budgetInactive ? '0.5' : 1,
              }}
            >
              Edit
            </Link>
          ) : null}
        </div>
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 15,
          opacity,
        }}
      >
        <div style={{ width: '15%' }}>
          <FormControl style={{ width: '100%', height: 17 }}>
            <InputLabel shrink>Issued</InputLabel>
          </FormControl>
          <div>
            {currencyFormat(donationCreditRule.totalIssued, donationCreditRule.currencyCode)}
          </div>
        </div>
        <div style={{ width: '15%', color: balanceLow ? red : undefined }}>
          <FormControl style={{ width: '100%', height: 17 }}>
            <InputLabel shrink>Balance</InputLabel>
          </FormControl>
          <div>
            {donationCreditRule.limitTotal
              ? currencyFormat(
                  donationCreditRule.limitTotal - donationCreditRule.totalIssued,
                  donationCreditRule.currencyCode
                )
              : '-'}
          </div>
        </div>
        <div
          style={{
            width: volunteeringRule ? '15%' : '35%',
          }}
        >
          <FormControl style={{ width: '100%', height: 17 }}>
            <InputLabel shrink>Total Limit</InputLabel>
          </FormControl>
          <div>
            {donationCreditRule.limitTotal
              ? currencyFormat(donationCreditRule.limitTotal, donationCreditRule.currencyCode)
              : '-'}
          </div>
        </div>
        {volunteeringRule && (
          <>
            <div style={{ width: '10%' }}>
              <FormControl style={{ width: '100%', height: 17 }}>
                <InputLabel shrink>Limit Per User</InputLabel>
              </FormControl>
              <div>
                {donationCreditRule.limitPerUser
                  ? currencyFormat(donationCreditRule.limitPerUser, donationCreditRule.currencyCode)
                  : '-'}
              </div>
            </div>
            <div style={{ width: '10%' }}>
              <FormControl style={{ width: '100%', height: 17 }}>
                <InputLabel shrink>Value</InputLabel>
              </FormControl>
              <div>
                {currencyFormat(
                  donationCreditRule.creditAmountPerHour,
                  donationCreditRule.currencyCode
                )}
                /h
              </div>
            </div>
          </>
        )}
        <div
          style={{
            width: '15%',
          }}
        >
          <FormControl style={{ width: '100%', height: 17 }}>
            <InputLabel shrink>Credits Issued</InputLabel>
          </FormControl>
          <div>{donationCreditRule.creditingTransactionsCount}</div>
        </div>
        <div
          style={{
            width: '20%',
          }}
        >
          {volunteeringRule && (
            <>
              <FormControl style={{ width: '100%', height: 17 }}>
                <InputLabel shrink>Start &amp; End Date</InputLabel>
              </FormControl>
              <div>
                {startingAt || endingAt ? (
                  <>
                    {startingAt
                      ? format(legacyParse(startingAt), convertTokens('YYYY-MM-DD'))
                      : 'N/A'}
                    {' – '}
                    {endingAt ? format(legacyParse(endingAt), convertTokens('YYYY-MM-DD')) : 'N/A'}
                  </>
                ) : (
                  'Not constrained'
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DonationCreditRuleCompact;
